import { Divider, Stack, Typography } from "@mui/material";
import { useCallback, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import type {
  ConditionConfiguration,
  ConditionDescriptor,
  ConditionFilterDescriptor,
} from "../../../../../../../../api/types/fieldConfigurationTypes.ts";
import { ConditionEqualityType } from "../../../../../../../../api/types/fieldConfigurationTypes.ts";
import { useNotificationContext } from "@entrilia/shared/contexts/NotificationContext.ts";
import { generateGuid } from "@entrilia/shared/utilities/generateGuid.ts";
import type {
  ConditionField,
  DimensionDescriptor,
  ReportType,
  StatementDataColumnConfiguration,
} from "../../../../../../../../api/types/svTypes.ts";
import { AreaItemType } from "../../../../../../../../api/types/svTypes.ts";
import { useBiCommonDataContext } from "../../../../contexts/BiCommonDataContext.ts";
import { useAppDispatch } from "../../../../store/hooks/useAppDispatch.ts";
import { selectAutoShowItemOptionsId } from "../../../../store/selectors/currentReportSelectors.ts";
import { selectStatementAxes, selectStatementDataColumns } from "../../../../store/selectors/fieldsStateSelectors.ts";
import { selectFieldCaptionLookup } from "../../../../store/selectors/metaDataSelectors.ts";
import { validateCondition } from "../../../../store/utils/fieldsUtils.ts";
import { createCurrentReportAction, createFinancialStatementAction } from "../../../../store/utils/storeUtils.ts";
import { getDimensionsAllowedInConditions } from "../../../../utilities/conditionDataTypes.ts";
import { DATA_COLUMN_LABEL, getDataColumnLabel } from "../../../viewer/financialStatement/statementColumnLabels.ts";
import ConditionFieldOptionPopup from "../../../viewer/common/conditions/ConditionFieldOptionPopup.tsx";
import type { ShowFieldOptionsSettings } from "../../../viewer/common/Types.ts";
import AddCustomConditions from "../../../viewer/common/values/AddCustomConditions.tsx";
import LinkConditions from "../../../viewer/common/values/LinkConditions.tsx";
import MeasureItemConditions from "../../../viewer/common/values/MeasureItemConditions.tsx";
import type { DateChannelAttempt } from "../utils/statementColumns.ts";
import { getUniqueLabel } from "../utils/uniqueLabel.ts";
import {
  findLinkedReportDate,
  getColumnDateDimensions,
  getConditionLabel,
  getConditionTokenName,
  getDateChannelRefusal,
  getDateConditionLabel,
  getLinkableColumnConditions,
  getOffsetWarning,
} from "../utils/statementColumns.ts";
import OffsetSelect from "./OffsetSelect.tsx";

const noNarrowingFilters: ConditionDescriptor[] = [];
const noExcludedConditions: ConditionField[] = [];

const toOwnConditionFields = (
  customConditions: ConditionConfiguration[],
  dimensions: DimensionDescriptor[],
  reportType: ReportType | undefined
): ConditionField[] =>
  customConditions.flatMap((config) => {
    const meta = dimensions.find((dimension) => dimension.name === config.filter.dimensionName);
    if (!meta) {
      return [];
    }
    const field: ConditionField = { config, meta, areaItemType: AreaItemType.VALUES };
    return [{ ...field, validation: validateCondition(field, [field], reportType) }];
  });

interface Props {
  column: StatementDataColumnConfiguration;
  readonly: boolean;
  hideAddActions?: boolean;
  disableAutoShowOptions?: boolean;
}

const DataColumnDatePanel = ({ column, readonly, hideAddActions, disableAutoShowOptions }: Props) => {
  const dispatch = useAppDispatch();
  const { sendNotification } = useNotificationContext();
  const { dimensions, conditions, reportType, isApiReport } = useBiCommonDataContext();
  const axes = useSelector(selectStatementAxes);
  const dataColumns = useSelector(selectStatementDataColumns);
  const getFieldCaption = useSelector(selectFieldCaptionLookup);
  const autoShowItemOptionsId = useSelector(selectAutoShowItemOptionsId);
  const [optionsTarget, setOptionsTarget] = useState<ShowFieldOptionsSettings<ConditionField>>();

  const dateDimensions = useMemo(
    () => getColumnDateDimensions(getDimensionsAllowedInConditions(dimensions, reportType)),
    [dimensions, reportType]
  );
  const linkableConditions = useMemo(() => getLinkableColumnConditions(conditions), [conditions]);
  const ownConditions = useMemo(
    () => toOwnConditionFields(column.customConditions, dimensions, reportType),
    [column.customConditions, dimensions, reportType]
  );
  const linkedConditions = useMemo(
    () =>
      conditions
        .filter((condition) => column.linkedConditions.includes(condition.config.guid))
        .map((condition) => ({ ...condition, linked: true })),
    [column.linkedConditions, conditions]
  );

  const updateColumn = useCallback(
    (changes: Partial<StatementDataColumnConfiguration>) =>
      dispatch(createFinancialStatementAction("updateDataColumn", { guid: column.guid, changes })),
    [column.guid, dispatch]
  );

  const handleSetAutoShowItemOptionsId = useCallback(
    (id?: string) => dispatch(createCurrentReportAction("setAutoShowItemOptionsId", id)),
    [dispatch]
  );

  const getLabelWithCondition = (condition: ConditionConfiguration): string | undefined =>
    getDateConditionLabel(column.customLabel, undefined, getConditionTokenName(condition, getFieldCaption));

  const getLabelWithoutCondition = (condition: ConditionConfiguration): string | undefined => {
    const label = getDateConditionLabel(
      column.customLabel,
      getConditionTokenName(condition, getFieldCaption),
      undefined
    );
    if (label !== undefined || dataColumns.length < 2) {
      return label;
    }
    const otherLabels = dataColumns
      .filter((other) => other.guid !== column.guid)
      .map(getDataColumnLabel);
    return getUniqueLabel(DATA_COLUMN_LABEL, otherLabels);
  };

  const isRefused = (attempt: DateChannelAttempt): boolean => {
    const refusal = getDateChannelRefusal(column, attempt, (guid) => {
      const condition = conditions.find((candidate) => candidate.config.guid === guid);
      return condition ? getConditionLabel(condition) : guid;
    });
    if (refusal) {
      sendNotification(refusal);
    }
    return refusal !== undefined;
  };

  const handleAddCondition = (dimension: DimensionDescriptor) => {
    if (isRefused("own")) {
      return;
    }
    const condition: ConditionConfiguration = {
      guid: generateGuid(),
      filter: { dimensionName: dimension.name, values: [], equalityType: ConditionEqualityType.Equal },
    };
    updateColumn({ customConditions: [condition], customLabel: getLabelWithCondition(condition) });
    handleSetAutoShowItemOptionsId(condition.guid);
  };

  const handleLinkCondition = (condition: ConditionField) => {
    if (!isRefused("link")) {
      updateColumn({
        linkedConditions: [condition.config.guid],
        customLabel: getLabelWithCondition(condition.config),
      });
    }
  };

  const handleRemoveOwnCondition = (item: ConditionField) => {
    setOptionsTarget(undefined);
    updateColumn({
      customConditions: column.customConditions.filter((condition) => condition.guid !== item.config.guid),
      customLabel: getLabelWithoutCondition(item.config),
    });
  };

  const handleUnlinkCondition = (item: ConditionField) =>
    updateColumn({
      linkedConditions: column.linkedConditions.filter((guid) => guid !== item.config.guid),
      dateOffsetMonths: undefined,
      customLabel: getLabelWithoutCondition(item.config),
    });

  const handleSaveFilter = (guid: string, filter: ConditionFilterDescriptor) => {
    setOptionsTarget(undefined);
    updateColumn({
      customConditions: column.customConditions.map((condition) =>
        condition.guid === guid ? { ...condition, filter: { ...condition.filter, ...filter } } : condition
      ),
    });
  };

  const reportDate = findLinkedReportDate(column, conditions);
  const offsetWarning = getOffsetWarning(column, conditions);
  const optionsCondition =
    optionsTarget && ownConditions.find((condition) => condition.config.guid === optionsTarget.field.config.guid);

  return (
    <Stack sx={{ width: "100%" }}>
      <Divider sx={{ borderStyle: "dashed" }} />
      <MeasureItemConditions
        conditions={ownConditions}
        canBeRemoved={!readonly}
        onRemoveItem={handleRemoveOwnCondition}
        onShowOptions={setOptionsTarget}
        disableEqualitySelection
        autoShowItemOptionsId={disableAutoShowOptions ? undefined : autoShowItemOptionsId}
        onSetAutoShowItemOptionsId={handleSetAutoShowItemOptionsId}
        readonly={readonly}
      />
      <MeasureItemConditions
        conditions={linkedConditions}
        canBeRemoved={!readonly}
        onRemoveItem={handleUnlinkCondition}
        disableEqualitySelection
        autoShowItemOptionsId={disableAutoShowOptions ? undefined : autoShowItemOptionsId}
        onSetAutoShowItemOptionsId={handleSetAutoShowItemOptionsId}
        readonly={readonly}
      />
      {reportDate && (
        <OffsetSelect
          dateOffsetMonths={column.dateOffsetMonths}
          axes={axes}
          readonly={readonly}
          onChange={(dateOffsetMonths) => updateColumn({ dateOffsetMonths })}
        />
      )}
      {offsetWarning && (
        <Typography variant="caption" color="warning" sx={{ px: 1.5, pb: 0.75 }}>
          {offsetWarning}
        </Typography>
      )}
      {!readonly && !hideAddActions && (
        <Stack sx={{ py: 0.5 }}>
          <AddCustomConditions
            dimensions={dateDimensions}
            conditions={noExcludedConditions}
            addCondition={handleAddCondition}
          />
          <LinkConditions
            linkableConditions={linkableConditions}
            disableEqualitySelection
            setLinkedCondition={handleLinkCondition}
          />
        </Stack>
      )}
      {optionsTarget?.ref && optionsCondition && (
        <ConditionFieldOptionPopup
          anchorEl={optionsTarget.ref}
          field={optionsCondition}
          narrowingFilters={noNarrowingFilters}
          readonlyCustomLabel
          canBeRemoved
          disableEqualitySelection
          disableReferenceDateSelection={isApiReport}
          save={(filter) => handleSaveFilter(optionsCondition.config.guid, filter)}
          onRemoveItem={handleRemoveOwnCondition}
          CustomId={undefined}
          readonly={readonly}
        />
      )}
    </Stack>
  );
};

export default DataColumnDatePanel;
